import { A } from "@solidjs/router";
import { createSignal, Match, onCleanup, onMount, Show, Switch } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { Money } from "../components/Money.tsx";
import {
  type BuyLike,
  type CheckoutMarker,
  clearCheckout,
  matchCheckoutBuy,
  readCheckout,
  resolveCheckoutPhase,
} from "../lib/checkout.ts";

/** How long to wait for the gateway's callback to mint before giving up. */
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 24; // ~60s

type Phase = "no-marker" | "waiting" | "posted" | "failed" | "timeout";

/**
 * Post-payment landing page (#150). The buyer returns here from the gateway's
 * hosted page while the capture callback mints coin server-side. We poll history
 * for the matching buy and surface pending → posted, or a failure/timeout — the
 * mint is asynchronous, so the coin may not be there the instant we land.
 */
export function BuyComplete() {
  const { client, ensureMaster } = useAuth();
  const [phase, setPhase] = createSignal<Phase>("waiting");
  const [amount, setAmount] = createSignal<string | null>(null);
  const [error, setError] = createSignal<unknown | null>(null);

  onMount(() => {
    const marker = readCheckout(sessionStorage);
    if (!marker) {
      setPhase("no-marker");
      return;
    }

    let polls = 0;
    let stopped = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
    };

    // Single-flight: a slow read can't overlap with the next tick (which would
    // stack requests and let a late response mutate phase after we've resolved).
    const poll = async (m: CheckoutMarker) => {
      if (stopped || inFlight) return;
      inFlight = true;
      let buy: BuyLike | null = null;
      try {
        const master = await ensureMaster();
        const { transactions } = await client.listTransactions({ walletId: master.id, limit: 25 });
        buy = matchCheckoutBuy(transactions, m);
        if (buy) setAmount(buy.amount);
        setError(null); // a successful read clears any earlier transient error
      } catch (err) {
        // Transient read error — keep polling; only surface if it persists.
        setError(err);
      } finally {
        inFlight = false;
      }
      polls += 1;
      const phase = resolveCheckoutPhase(buy, polls >= MAX_POLLS);
      setPhase(phase);
      if (phase === "posted" || phase === "failed") {
        clearCheckout(sessionStorage);
        stop();
        return;
      }
      if (phase === "timeout") {
        stop();
        return;
      }
      if (!stopped) timer = setTimeout(() => void poll(m), POLL_INTERVAL_MS);
    };

    void poll(marker);
    onCleanup(stop);
  });

  return (
    <section class="card">
      <h2>Completing your purchase</h2>
      <Switch>
        <Match when={phase() === "waiting"}>
          <p class="muted">
            Payment received — crediting your coin. This usually takes a few seconds.
          </p>
          <ErrorBanner error={error()} />
        </Match>

        <Match when={phase() === "posted"}>
          <p>
            Coin credited ✅{" "}
            <Show when={amount()}>
              {(a) => (
                <>
                  <Money amount={a()} /> is now in your wallet.
                </>
              )}
            </Show>
          </p>
          <div class="row">
            <A class="primary" href="/">
              Back to wallet
            </A>
            <A class="secondary" href="/buy">
              Buy more
            </A>
          </div>
        </Match>

        <Match when={phase() === "failed"}>
          <p>Payment didn't go through, so no coin was credited.</p>
          <A class="primary" href="/buy">
            Try again
          </A>
        </Match>

        <Match when={phase() === "timeout"}>
          <p class="muted">
            This is taking longer than usual. Your payment may still be processing — check your{" "}
            <A href="/history">history</A> in a minute.
          </p>
          <A class="primary" href="/">
            Back to wallet
          </A>
        </Match>

        <Match when={phase() === "no-marker"}>
          <p class="muted">No purchase in progress.</p>
          <A class="primary" href="/buy">
            Buy GG-Coin
          </A>
        </Match>
      </Switch>
    </section>
  );
}
